import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";

// A bottom-anchored modal sheet. Tap-outside or swipe-down closes (browser handles
// gestures via the backdrop click; full swipe gestures are Phase-8 polish).
export function Sheet({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="sheet-backdrop" onClick={onClose}>
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet__handle" aria-hidden="true" />
        {title && <h2 className="sheet__title">{title}</h2>}
        <div className="sheet__body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
